import { ShaderMaterial } from "@babylonjs/core/Materials/shaderMaterial.js";
import { Env } from "../settings/Env";
import { Color4 } from "@babylonjs/core/Maths/math.color.js";
import { PhotoDome } from "@babylonjs/core/Helpers/photoDome.js";
import { MeshName } from "./MeshName";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode.js";
import { waitFor } from "../utils/AsyncUtils";
import { Vector3 } from "@babylonjs/core/Maths/math.vector.js";
import { SceneLoader } from "@babylonjs/core/Loading/sceneLoader.js";
const deg2rad = Math.PI / 180;
export class EnvironmentView {
    constructor(scene, guiRootNode, lightsToExclude) {
        this._envRootNode = null;
        this._envSceneNode = null;
        this._dome = null;
        this._previousLoaderResult = null;
        this._currentLoaderResult = null;
        this._scene = scene;
        this._guiRootNode = guiRootNode;
        this._lightsToExclude = lightsToExclude;
        this._recreateEnvRootNode();
        this._skyboxMaterial = new ShaderMaterial("AdvancedDynamicTextureMaterial", scene, {
            vertexSource: `precision highp float;
                    attribute vec3 position;
                    attribute vec2 uv;
                    uniform mat4 worldViewProjection;
                    varying vec2 vUV;
                    void main(void) {
                        gl_Position = worldViewProjection * vec4(position, 1.0);
                        vUV = uv;
                    }`,
            fragmentSource: `precision highp float;
                    varying vec2 vUV;
                    uniform vec4 backgroundColor;
                    uniform int pointsLength;
                    uniform vec4 points[16];
                    uniform int useGradient;
                    void main(void) {
                        if(useGradient == 0)
                        {
                            gl_FragColor = backgroundColor;
                            return;
                        }
                        int maxIndex = pointsLength > 16 ? 15 : pointsLength - 1;
                        for(int i = 0; i < 16; i++)
                        {
                            if (i > pointsLength - 1) break;
                            int next = i < maxIndex ? i + 1 : i;
                            if(points[next].a < vUV.y || points[i].a > vUV.y )
                                continue;
                            vec4 prevPoint = points[i];
                            vec4 nextPoint = points[next];
                            float value = (vUV.y  - prevPoint.a) / (nextPoint.a - prevPoint.a);
                            gl_FragColor = mix(prevPoint, nextPoint, value);
                            gl_FragColor.a = 1.0;
                            break;
                        }
                    }`,
        }, {
            attributes: ["position", "normal", "uv"],
            uniforms: ["world", "worldView", "worldViewProjection", "view", "projection", "points", "useGradient"],
        });
        const backgroundColor = Color4.FromHexString(Env.scene.skyboxColor);
        this._skyboxMaterial.setColor4("backgroundColor", backgroundColor);
        this._skyboxMaterial.setInt("pointsLength", Env.scene.skyboxGradient.length);
        const gradient = Env.scene.skyboxGradient.map(c => {
            const color = Color4.FromHexString(c.color);
            color.a = c.offset;
            return color;
        });
        this._skyboxMaterial.setColor4Array("points", gradient);
    }
    async preloadEnvironmentAsync(sceneEnvironment) {
        this._currentLoaderResult = null;
        try {
            if (sceneEnvironment.scene) {
                this._currentLoaderResult = await SceneLoader.ImportMeshAsync("", sceneEnvironment.scene.url, "", this._scene);
                this._setLoaderResultVisibility(this._currentLoaderResult, false);
                this._currentLoaderResult.meshes.forEach(x => x.isPickable = false);
            }
            return true;
        }
        catch (e) {
            console.error(e);
            return false;
        }
    }
    async updateEnvironmentAsync(sceneEnvironment) {
        // Hierarchy and operations:
        // ▔▔▔▔▔▔▔▔▔▔▔▔▔▔▔▔▔▔▔▔▔▔▔▔▔
        //  guiRoot (+rotation => to recenter the whole GUI)
        //   └─ envRoot
        //      ├─ envScene (+rotation, +position => to set GLB rotation and position)
        //      │   └─ GLB
        //      └─ dome (+rotation => to rotate the skybox)
        this._recreateEnvRootNode();
        try {
            const domeTextureUrl = sceneEnvironment.skybox?.photoDome
                ? sceneEnvironment.skybox.photoDome.url
                : Env.scene.emptyDomeTextureUrl;
            const domeSize = sceneEnvironment.skybox?.photoDome?.size ?? 50;
            this._dome = new PhotoDome("skybox", domeTextureUrl, { resolution: 32, size: domeSize }, this._scene);
            this._dome.imageMode = sceneEnvironment.skybox?.photoDome?.imageMode
                ? sceneEnvironment.skybox?.photoDome?.imageMode
                : PhotoDome.MODE_MONOSCOPIC;
            this._dome.parent = this._envRootNode;
            this._dome.rotation.y = (sceneEnvironment.skybox?.photoDome?.rotation ?? 0) * deg2rad;
            this._dome.mesh.name = MeshName.backgroundSkybox;
            let domeLoaded = false;
            this._dome.onLoadObservable.addOnce(() => domeLoaded = true);
            await waitFor(() => domeLoaded, 10_000);
            if (sceneEnvironment.skybox?.color) {
                const backgroundColor = Color4.FromHexString(sceneEnvironment.skybox.color);
                this._skyboxMaterial.setColor4("backgroundColor", backgroundColor);
                this._skyboxMaterial.setInt("useGradient", 0);
                this._dome.mesh.material = this._skyboxMaterial;
            }
            else if (sceneEnvironment.skybox?.gradient) {
                const gradient = sceneEnvironment.skybox.gradient.map(c => {
                    const color = Color4.FromHexString(c.color);
                    color.a = c.offset;
                    return color;
                });
                this._skyboxMaterial.setInt("pointsLength", sceneEnvironment.skybox.gradient.length);
                this._skyboxMaterial.setColor4Array("points", gradient);
                this._skyboxMaterial.setInt("useGradient", 1);
                this._dome.mesh.material = this._skyboxMaterial;
            }
            // Dispose previously loaded objects:
            if (this._previousLoaderResult !== null) {
                this._clearLoaderResult(this._previousLoaderResult);
                this._previousLoaderResult = null;
            }
            // Activate currently loaded objects:
            if (this._currentLoaderResult !== null) {
                this._addToEnvSceneNode(this._currentLoaderResult);
                if (this._envSceneNode !== null) {
                    this._envSceneNode.rotation.y = (sceneEnvironment.scene?.rotation ?? 0) * deg2rad;
                    this._envSceneNode.position = EnvironmentView.createVector3(sceneEnvironment.scene?.position);
                }
                this._enableLightsOnlyForEnvironmentMeshes(this._currentLoaderResult);
                this._lightsToExclude.forEach(light => light.excludedMeshes = light.excludedMeshes.concat(this._currentLoaderResult.meshes));
                this._setLoaderResultVisibility(this._currentLoaderResult, true);
            }
            // Save currently active object for future deactivation:
            this._previousLoaderResult = this._currentLoaderResult;
            return true;
        }
        catch (e) {
            this._dome?.dispose();
            this._dome = null;
            this._recreateEnvRootNode();
            console.error(e);
            return false;
        }
    }
    static createVector3(array) {
        if (array === undefined || array.length !== 3)
            return Vector3.Zero();
        return new Vector3(array[0], array[1], array[2]);
    }
    _enableLightsOnlyForEnvironmentMeshes(loaderResult) {
        loaderResult.lights.forEach(l => {
            l.includedOnlyMeshes = loaderResult.meshes;
        });
    }
    _recreateEnvRootNode() {
        this._envRootNode?.dispose(false, true);
        this._envRootNode = new TransformNode("envRootNode", this._scene);
        this._envRootNode.parent = this._guiRootNode;
        this._envSceneNode?.dispose(false, true);
        this._envSceneNode = new TransformNode("envSceneNode", this._scene);
        this._envSceneNode.parent = this._envRootNode;
    }
    _addToEnvSceneNode(loaderResult) {
        const rootNode = loaderResult.meshes.find(x => x.name === "__root__");
        if (rootNode && this._envSceneNode) {
            rootNode.parent = this._envSceneNode;
        }
    }
    _clearLoaderResult(loaderResult) {
        loaderResult.meshes.forEach(x => x.dispose(false, true));
        loaderResult.particleSystems.forEach(x => x.dispose(true));
        loaderResult.skeletons.forEach(x => x.dispose());
        loaderResult.animationGroups.forEach(x => x.dispose());
        loaderResult.transformNodes.forEach(x => x.dispose(false, true));
        loaderResult.geometries.forEach(x => x.dispose());
        loaderResult.lights.forEach(x => x.dispose(false, true));
    }
    _setLoaderResultVisibility(loaderResult, value) {
        loaderResult.meshes.forEach(x => x.isVisible = value);
        // loaderResult.particleSystems.forEach(x => );    // nothing to activate/deactivate
        // loaderResult.skeletons.forEach(x => );          // nothing to activate/deactivate
        // loaderResult.animationGroups.forEach(x => );    // nothing to activate/deactivate
        // loaderResult.transformNodes.forEach(x => );     // nothing to activate/deactivate
        // loaderResult.geometries.forEach(x => );         // nothing to activate/deactivate
        loaderResult.lights.forEach(x => x.setEnabled(value));
    }
}
